import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { publicApi } from '@/lib/api';
import { CONTACT_REASONS } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { CONTACT } from '@/constants/testIds';

export default function ContactPage() {
  const { config } = useOutletContext() || {};
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('general');
  const [message, setMessage] = useState('');
  const [hp, setHp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useSeo({
    title: 'Contact | A Guy Who Hunts',
    description: 'Reach out for partnerships, media requests, collaborations, or a friendly note.',
  });

  const submit = async (e) => {
    e.preventDefault();
    setResult(null);
    if (!name.trim() || !email.trim() || !message.trim()) {
      setResult({ ok: false, message: 'Please fill in name, email, and a message.' });
      return;
    }
    if (message.trim().length < 8) {
      setResult({ ok: false, message: 'Please add a bit more to your message.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicApi.contact({ name, email, reason, message, honeypot: hp });
      setResult({ ok: true, message: res.message || 'Message received.' });
      toast.success('Message sent.');
      setName('');
      setEmail('');
      setMessage('');
      setReason('general');
    } catch (err) {
      const message = err?.response?.data?.detail || 'Could not send your message. Please try again.';
      setResult({ ok: false, message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="kicker">Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight">
            Say hello.
          </h1>
          <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-2xl">
            Brand partnership, podcast or media request, collaboration idea, or just a friendly note — use the form below
            or email {config?.contact_email || 'hello@aguywhohunts.com'}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14">
        <form
          onSubmit={submit}
          className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 sm:p-8 card-elev"
          noValidate
        >
          <input
            type="text"
            name="website"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="contact-name" className="kicker mb-2 block">Name</Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={CONTACT.nameInput}
                required
              />
            </div>
            <div>
              <Label htmlFor="contact-email" className="kicker mb-2 block">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={CONTACT.emailInput}
                required
              />
            </div>
          </div>
          <div className="mt-5">
            <Label className="kicker mb-2 block">Reason for contact</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid={CONTACT.reasonSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-5">
            <Label htmlFor="contact-message" className="kicker mb-2 block">Message</Label>
            <Textarea
              id="contact-message"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid={CONTACT.messageInput}
              required
            />
          </div>

          {result && result.ok && (
            <p
              data-testid={CONTACT.successMessage}
              className="mt-4 text-sm text-[hsl(var(--primary))]"
              role="status"
              aria-live="polite"
            >
              {result.message}
            </p>
          )}
          {result && !result.ok && (
            <p
              data-testid={CONTACT.errorMessage}
              className="mt-4 text-sm text-[hsl(var(--destructive))]"
              role="alert"
            >
              {result.message}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="submit" size="lg" disabled={submitting} data-testid={CONTACT.submitButton}>
              {submitting ? 'Sending…' : 'Send message'}
            </Button>
            <p className="text-xs text-[hsl(var(--muted-foreground))] self-center">
              We never share your info. This form is protected against automated spam.
            </p>
          </div>
        </form>
      </section>
    </div>
  );
}
